/**
 * The colour a gradient shows at one point of a box.
 *
 * Block text paints a gradient the proper way — CSS clips it to the glyphs, the
 * export builds a CanvasGradient over the same box. Text on a PATH cannot: every
 * glyph is drawn under its own translate+rotate, so a gradient built once would
 * be re-transformed per letter and shear. Sampling the gradient at each glyph's
 * own centre gives the same colour ramp along the run, and — the reason it is a
 * shared helper — gives the editor's SVG and the export's canvas the identical
 * per-glyph colour, so the two surfaces cannot drift.
 */

import { GRADIENT_ANGLE_DEFAULT } from '../panels/propertyControls';

const HEX = /^#?([0-9a-f]{3,8})$/i;

/** '#abc' / '#aabbcc' / '#aabbccdd' → [r, g, b, a]; anything else → null. */
function parseColor(value) {
    const m = HEX.exec(String(value || '').trim());
    if (!m) return null;
    let hex = m[1];
    if (hex.length === 3 || hex.length === 4) hex = [...hex].map((c) => c + c).join('');
    if (hex.length !== 6 && hex.length !== 8) return null;
    return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
        hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
    ];
}

const toHex = (n) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0');

function mix(a, b, t) {
    const rgba = [0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * t);
    const alpha = a[3] + (b[3] - a[3]) * t;
    const hex = `#${rgba.map(toHex).join('')}`;
    return alpha >= 1 ? hex : `${hex}${toHex(alpha * 255)}`;
}

/**
 * @param paint  {type, color, color1, color2, angle, centerX, centerY} — the shape
 *               every fill in the editor is described with (see textFillPaint).
 * @param x,y    the point, in the box's own coordinates (0,0 = top left)
 * @param w,h    the box the gradient is painted across
 */
export function sampleGradientAt(paint, x, y, w, h) {
    const type = paint?.type || 'solid';
    if (type !== 'linear' && type !== 'radial') return paint?.color || null;

    const c1 = parseColor(paint.color1 || paint.color || '#ffffff');
    const c2 = parseColor(paint.color2 || '#000000');
    if (!c1 || !c2) return paint.color || null;

    if (type === 'radial') {
        // CSS radial-gradient(circle at cx% cy%, c1, c2) with the default
        // farthest-corner sizing the rest of the editor assumes: half the diagonal.
        const cx = (paint.centerX != null ? Number(paint.centerX) : 50) / 100 * w;
        const cy = (paint.centerY != null ? Number(paint.centerY) : 50) / 100 * h;
        const radius = Math.sqrt(w * w + h * h) / 2 || 1;
        const t = Math.min(1, Math.hypot(x - cx, y - cy) / radius);
        return mix(c1, c2, t);
    }

    // CSS linear-gradient: 0deg points UP and angles run clockwise. The colour at a
    // point is how far it sits along that axis, measured from the box centre.
    const rad = ((Number(paint.angle) || GRADIENT_ANGLE_DEFAULT) * Math.PI) / 180;
    const ux = Math.sin(rad);
    const uy = -Math.cos(rad);
    const len = Math.abs(w * ux) + Math.abs(h * uy) || 1;
    const proj = (x - w / 2) * ux + (y - h / 2) * uy;
    return mix(c1, c2, Math.min(1, Math.max(0, proj / len + 0.5)));
}
